import { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  notification,
  Row,
  Col,
  Switch,
  Skeleton,
} from "antd";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useAuth } from "../../hooks/useAuth";
import queryClient from "../../queryClient";
import { filterOption, handleIntegerKeyDown, createIntegerChangeHandler } from "../../utils/textUtils";
import { DATE_PICKER_FORMAT } from "../../constants/dateConstants";
import { toUTCMidnight, parseDateOnlyForPicker } from "../../utils/dateUtils";
import AttachmentsSection from "../AttachmentsSection";
import { fetchVendors } from "../../services/vendorsAPI";
import {
  createContract,
  fetchContract,
  fetchNextContractNumber,
  updateContract,
} from "../../services/contractsAPI";
import TextArea from "antd/es/input/TextArea";

import dayjs from "dayjs";
import ChangeOrdersTable from "./ChangeOrdersTable";
import AttachmentsView from "../AttachmentsView";
import { getChangeOrderLabel, isTimeChangeOrdersEnabled } from "../../utils/projectUtils";

const NewOrUpdateContract = ({
  visible,
  onClose,
  contractId,
  isViewMode,
  defaultContractType = null,
  hasOwnerContract = false,
}) => {
  const [form] = Form.useForm();
  const { selectedProject } = useAuth();
  const coLabel = getChangeOrderLabel(selectedProject);
  const timeChangeOrdersEnabled = isTimeChangeOrdersEnabled(selectedProject);
  const projectId = selectedProject.project_id;
  const [isVisibleNewOrUpdateChangeOrder, setIsVisibleNewOrUpdateChangeOrder] =
    useState(false);
  const contractTypes = [
    { label: "Contract with Owner", value: "owner" },
    { label: "Subcontract", value: "subcontract" },
    { label: "Purchase Order", value: "purchase_order" },
  ];

  const statuses = [
    { label: "Draft", value: "draft" },
    { label: "Out For Bid", value: "out_for_bid" },
    { label: "Out For Signature", value: "out_for_signature" },
    { label: "Approved", value: "approved" },
    { label: "Complete", value: "complete" },
    { label: "Terminated", value: "terminated" },
    { label: "Void", value: "void" },
    { label: "Executed", value: "executed" },
  ];

  // const ssovTypes = [
  //   { label: "Draft", value: "draft" },
  //   { label: "None", value: "none" },
  // ];

  const { data: contractData, isLoading: isContractLoading } = useQuery({
    queryKey: ["contracts", contractId, projectId],
    queryFn: () =>
      fetchContract({
        contractId,
        projectId,
      }),
    enabled: !!contractId,
  });

  const { data: projectVendors } = useQuery({
    queryKey: ["vendors", projectId, 1, 1000],
    queryFn: () => fetchVendors(projectId, 1, 1000),
    enabled: !!projectId,
  });

  useEffect(() => {
    if (projectId && !contractId) {
      fetchNextContractNumber({ projectId }).then((data) => {
        form.setFieldsValue({
          number: data.next_number.toString(),
          ...(defaultContractType
            ? { contract_type: defaultContractType }
            : {}),
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, contractId, defaultContractType]);

  useEffect(() => {
    if (contractData) {
      form.setFieldsValue({
        ...contractData,
        _id: contractData._id,
        contract_date: contractData.contract_date
          ? dayjs(contractData.contract_date)
          : null,
        contract_start_date: contractData.contract_start_date
          ? parseDateOnlyForPicker(contractData.contract_start_date)
          : null,
        contract_estimated_completion_date:
          contractData.contract_estimated_completion_date
            ? parseDateOnlyForPicker(
                contractData.contract_estimated_completion_date,
              )
            : null,
        attachments: contractData.attachments?.map((file) => file) || [],
        contract_company_id: contractData.contract_company?.id || null,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractData]);

  const mutation = useMutation({
    mutationFn: ({ contractData }) =>
      createContract({
        ...contractData,
        contract_date: contractData.contract_date
          ? contractData.contract_date.toISOString()
          : null,
        contract_start_date: contractData.contract_start_date
          ? toUTCMidnight(contractData.contract_start_date)
          : null,
        contract_estimated_completion_date:
          contractData.contract_estimated_completion_date
            ? toUTCMidnight(contractData.contract_estimated_completion_date)
            : null,
        attachments: contractData.attachments?.map((file) => file) || [],
      }),
    onSuccess: () => {
      queryClient.invalidateQueries(["contracts", projectId]);
      onClose();
      form.resetFields();
      notification.success({ message: "Contract created successfully" });
    },
    onError: (error) => {
      notification.error({
        message: "Failed to create Contract",
        description: error.message,
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ contractId, contractData }) =>
      updateContract(contractId, {
        ...contractData,
        contract_date: contractData.contract_date
          ? contractData.contract_date.toISOString()
          : null,
        contract_start_date: contractData.contract_start_date
          ? toUTCMidnight(contractData.contract_start_date)
          : null,
        contract_estimated_completion_date:
          contractData.contract_estimated_completion_date
            ? toUTCMidnight(contractData.contract_estimated_completion_date)
            : null,
        attachments: contractData.attachments?.map((file) => file) || [],
      }),
    onSuccess: () => {
      queryClient.invalidateQueries(["contracts", projectId]);
      onClose();
      form.resetFields();
      notification.success({ message: "Contract updated successfully" });
    },
    onError: (error) => {
      notification.error({
        message: "Failed to update Contract",
        description: error.message,
      });
    },
  });

  const toOptionalInt = (value) => {
    if (value === "" || value === null || value === undefined) return null;
    const n = Number(value);
    return Number.isNaN(n) ? null : n;
  };

  const handleCreateContract = async (values) => {
    const contractData = {
      ...values,
      project_id: projectId,
      attachments: values.attachments?.map((file) => file) || [],
    };
    if (timeChangeOrdersEnabled) {
      contractData.duration = toOptionalInt(values.duration);
    } else {
      delete contractData.duration;
    }
    mutation.mutate({ contractData });
  };

  const handleUpdateContract = async (values) => {
    const contractData = {
      ...values,
      project_id: projectId,
      attachments: values.attachments?.map((file) => file) || [],
    };
    if (timeChangeOrdersEnabled) {
      contractData.duration = toOptionalInt(values.duration);
    } else {
      delete contractData.duration;
    }
    updateMutation.mutate({
      contractId: contractData._id,
      contractData,
    });
  };

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal open={visible} onCancel={handleClose} footer={null} width={800}>
      {contractId && isContractLoading ? (
        <Skeleton active />
      ) : (
        <>
          <h1 className="text-2xl font-bold mb-4">
            {contractData && isViewMode
              ? "View Contract"
              : contractData && !isViewMode
              ? "Update Contract"
              : "Create New Contract"}
          </h1>
          <Form
            form={form}
            onFinish={
              !contractData && !isViewMode
                ? handleCreateContract
                : handleUpdateContract
            }
            layout="vertical"
          >
            <Form.Item name="_id" hidden>
              <Input disabled={isViewMode} />
            </Form.Item>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Contract Type"
                  name="contract_type"
                  rules={[{ required: true, message: "Please select type" }]}
                >
                  <Select
                    disabled={isViewMode || !!defaultContractType || !!contractId}
                    showSearch
                    optionLabel="name"
                    optionValue="value"
                    optionFilterProp="children"
                    filterOption={filterOption}
                    options={
                      defaultContractType === "owner"
                        ? contractTypes.filter((type) => type.value === "owner")
                        : contractTypes.filter(
                            (type) => type.value !== "owner" || !hasOwnerContract,
                          )
                    }
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Status"
                  name="status"
                  rules={[{ required: true, message: "Please Select Status!" }]}
                >
                  <Select
                    disabled={isViewMode}
                    showSearch
                    optionLabel="name"
                    optionValue="value"
                    optionFilterProp="children"
                    filterOption={filterOption}
                    options={statuses}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Contract Company"
                  name="contract_company_id"
                  rules={[
                    {
                      required: true,
                      message: "Please Select Contract Company",
                    },
                  ]}
                >
                  <Select
                    disabled={isViewMode}
                    showSearch
                    optionLabel="name"
                    optionValue="_id"
                    optionFilterProp="children"
                    filterOption={filterOption}
                    options={projectVendors?.vendors?.map((vendor) => ({
                      value: vendor.id,
                      label: vendor.name,
                    }))}
                    filter
                    filterBy="name"
                    emptyFilterMessage="No companies found"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Title"
                  name="title"
                  rules={[{ required: true, message: "Please input the title!" }]}
                >
                  <Input disabled={isViewMode} />
                </Form.Item>
              </Col>
              {/* <Col span={12}>
                <Form.Item
                  label="SSOV Status"
                  name="ssov_status"
                  rules={[
                    { required: true, message: "Please Select SSOV Status!" },
                  ]}
                >
                  <Select
                    showSearch
                    optionLabel="name"
                    optionValue="value"
                    optionFilterProp="children"
                    filterOption={filterOption}
                    options={ssovTypes}
                  />
                </Form.Item>
              </Col> */}
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Number"
                  name="number"
                  rules={[{ required: true, message: "Please input the number!" }]}
                >
                  <Input disabled={isViewMode} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Contract Date" name="contract_date">
                  <DatePicker className="w-full" format={DATE_PICKER_FORMAT} disabled={isViewMode} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Start Date" name="contract_start_date">
                  <DatePicker className="w-full" format={DATE_PICKER_FORMAT} disabled={isViewMode} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Estimated Completion Date"
                  name="contract_estimated_completion_date"
                >
                  <DatePicker className="w-full" format={DATE_PICKER_FORMAT} disabled={isViewMode} />
                </Form.Item>
              </Col>
            </Row>

            {contractData?._id && (
              <ChangeOrdersTable
                contractId={contractData?._id}
                isViewModeContract={isViewMode}
                isVisibleNewOrUpdateChangeOrder={isVisibleNewOrUpdateChangeOrder}
                setIsVisibleNewOrUpdateChangeOrder={
                  setIsVisibleNewOrUpdateChangeOrder
                }
              />
            )}
            {/*<Row gutter={16}>
               <Col span={12}>
                <Form.Item label="Inclusions" name="inclusions" className="w-full">
                  <TextArea />
                </Form.Item>
              </Col> */}
            {/* <Col span={12}>
                <Form.Item label="Exclusions" name="exclusions">
                  <TextArea />
                </Form.Item>
              </Col>
            </Row> */}

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Original Contract Amount"
                  name="original_contract_amount"
                  rules={[
                    {
                      required: true,
                      message: "Please input the original contract amount!",
                    },
                  ]}
                >
                  <Input type="number" disabled={isViewMode} />
                </Form.Item>
              </Col>
              {contractData?._id && (
                <Col span={12}>
                  <Form.Item
                    label={`Approved ${coLabel} Amount`}
                    name="approved_change_orders"
                    rules={[
                      {
                        required: true,
                        message: `Please input the Approved ${coLabel} Amount!`,
                      },
                    ]}
                  >
                    <Input type="number" disabled />
                  </Form.Item>
                </Col>
              )}
            </Row>
            {contractData?._id && (
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label={`Pending ${coLabel} Amount`}
                    name="pending_change_orders"
                    rules={[
                      {
                        required: true,
                        message: `Please input the Pending ${coLabel} Amount!`,
                      },
                    ]}
                  >
                    <Input type="number" disabled />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Revised Contract Amount"
                    name="revised_contract_amount"
                    rules={[
                      {
                        required: true,
                        message: "Please input the Revised Contract Amount!",
                      },
                    ]}
                  >
                    <Input type="number" disabled />
                  </Form.Item>
                </Col>
              </Row>
            )}

            {timeChangeOrdersEnabled ? (
              <>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      label="Duration (in days)"
                      name="duration"
                    >
                      <Input
                        type="number"
                        min={0}
                        step={1}
                        disabled={isViewMode}
                        onKeyDown={handleIntegerKeyDown}
                        onChange={createIntegerChangeHandler(form, "duration")}
                      />
                    </Form.Item>
                  </Col>
                  {contractData?._id && (
                    <Col span={12}>
                      <Form.Item
                        label={`Approved ${coLabel} Days`}
                        name="approved_change_order_days"
                      >
                        <Input type="number" disabled />
                      </Form.Item>
                    </Col>
                  )}
                </Row>
                {contractData?._id && (
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        label="Revised Duration"
                        name="revised_duration"
                      >
                        <Input type="number" disabled />
                      </Form.Item>
                    </Col>
                  </Row>
                )}
              </>
            ) : (
              <Form.Item name="duration" hidden>
                <Input />
              </Form.Item>
            )}

            <Row gutter={16}>
              {/* <Col span={12}>
                <Form.Item
                  label="Description"
                  name="description"
                  className="w-full"
                >
                  <TextArea />
                </Form.Item>
              </Col> */}
              <Col span={24}>
                <Form.Item label="Notes" name="notes" className="w-full">
                  <TextArea disabled={isViewMode} />
                </Form.Item>
              </Col>
            </Row>
            {/* <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  label="Users For Visibility"
                  name="visibility"
                  rules={[
                    {
                      required: true,
                      message: "Please select Users For Visibility!",
                    },
                  ]}
                >
                  <Select
                    disabled={isViewMode}
                    showSearch
                    optionFilterProp="children"
                    filterOption={filterOption}
                    options={projectUsers?.data?.map((user) => ({
                      value: user.id,
                      label: `${user.username} ${
                        user.vendor_name ? `(${user.vendor_name})` : ""
                      }`,
                    }))}
                    mode="multiple"
                  />
                </Form.Item>
              </Col>
            </Row> */}
            <Form.Item name="attachments" label="Attachments">
              {isViewMode && (
                <AttachmentsView attachments={contractData?.attachments} />
              )}
              {!isViewMode && (
                <AttachmentsSection projectId={projectId} disabled={isViewMode} />
              )}
            </Form.Item>

            {/* <Form.Item>
              <div className="mb-2 flex items-center">
                <Form.Item
                  name="executed"
                  valuePropName="checked"
                  initialValue={true}
                  noStyle
                >
                  <Switch
                    defaultChecked={true}
                    onChange={(checked) =>
                      form.setFieldsValue({ executed: checked })
                    }
                  />
                </Form.Item>
                <span className="ml-2">Executed</span>
              </div>
            </Form.Item>*/}
            {contractData && isViewMode ? null : (
              <Form.Item>
                <Button
                  type="primary"
                  onClick={() => form.submit()}
                  loading={mutation.isPending}
                  className="w-full mt-4"
                >
                  {contractData && isViewMode
                    ? "View"
                    : contractData && !isViewMode
                    ? "Update"
                    : "Create"}
                </Button>
              </Form.Item>
            )}
          </Form>
        </>
      )}
    </Modal>
  );
};

export default NewOrUpdateContract;
